import { CloseOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { useAdminVisitedTabs } from './useAdminVisitedTabs';
import type { AdminVisitedTab } from './visitedTabs';

export type AdminVisitedTabsController = ReturnType<typeof useAdminVisitedTabs>;

const TABS_GAP = 7;
const TABS_HORIZONTAL_PADDING = 4;

function sameIds(left: string[] | null, right: string[]): boolean {
  return left !== null
    && left.length === right.length
    && left.every((id, index) => id === right[index]);
}

export function AdminVisitedTabs({
  controller,
}: {
  controller: AdminVisitedTabsController;
}) {
  const { tabs, activeTabId, activateTab, closeTab } = controller;
  const tabsViewportRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [visibleTabIds, setVisibleTabIds] = useState<string[] | null>(null);

  const recalculateVisibleTabs = useCallback(() => {
    const viewport = tabsViewportRef.current;
    const measure = measureRef.current;
    if (!viewport || !measure || tabs.length === 0) {
      setVisibleTabIds(current => (tabs.length === 0 && !sameIds(current, []) ? [] : current));
      return;
    }

    const widths = new Map<string, number>();
    measure.querySelectorAll<HTMLElement>('[data-tab-measure-id]').forEach(element => {
      const tabId = element.dataset.tabMeasureId;
      if (tabId) widths.set(tabId, element.offsetWidth);
    });

    const moreButtonWidth = measure.querySelector<HTMLElement>('[data-more-measure]')?.offsetWidth;
    if (widths.size !== tabs.length || !moreButtonWidth) return;

    const availableWidth = Math.max(0, viewport.clientWidth - TABS_HORIZONTAL_PADDING);
    const totalWidth = tabs.reduce((sum, tab) => sum + (widths.get(tab.id) ?? 0), 0)
      + Math.max(0, tabs.length - 1) * TABS_GAP;

    if (totalWidth <= availableWidth) {
      const nextIds = tabs.map(tab => tab.id);
      setVisibleTabIds(current => (sameIds(current, nextIds) ? current : nextIds));
      return;
    }

    const capacity = Math.max(0, availableWidth - moreButtonWidth - TABS_GAP);
    const candidates = [...tabs].sort((left, right) => {
      if (left.id === activeTabId) return -1;
      if (right.id === activeTabId) return 1;
      return right.lastVisitedAt - left.lastVisitedAt;
    });
    const selected = new Set<string>();
    let usedWidth = 0;

    candidates.forEach(tab => {
      const tabWidth = widths.get(tab.id) ?? 0;
      const nextWidth = selected.size === 0 ? tabWidth : usedWidth + TABS_GAP + tabWidth;
      const isActive = tab.id === activeTabId;
      if (isActive || nextWidth <= capacity) {
        selected.add(tab.id);
        usedWidth = nextWidth;
      }
    });

    if (selected.size === 0 && candidates[0]) selected.add(candidates[0].id);

    const nextIds = tabs.filter(tab => selected.has(tab.id)).map(tab => tab.id);
    setVisibleTabIds(current => (sameIds(current, nextIds) ? current : nextIds));
  }, [activeTabId, tabs]);

  useLayoutEffect(() => {
    recalculateVisibleTabs();
    const viewport = tabsViewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(recalculateVisibleTabs);
    observer.observe(viewport);
    if (measureRef.current) observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [recalculateVisibleTabs]);

  const visibleTabs = useMemo(() => {
    if (visibleTabIds === null) return tabs;
    const visibleIds = new Set(visibleTabIds);
    return tabs.filter(tab => visibleIds.has(tab.id));
  }, [tabs, visibleTabIds]);
  const visibleIds = useMemo(() => new Set(visibleTabs.map(tab => tab.id)), [visibleTabs]);
  const hiddenTabs = useMemo(
    () => tabs.filter(tab => !visibleIds.has(tab.id)),
    [tabs, visibleIds],
  );

  const renderTab = (tab: AdminVisitedTab, measure = false) => {
    const active = tab.id === activeTabId;
    return (
      <div
        key={tab.id}
        data-tab-measure-id={measure ? tab.id : undefined}
        className={`admin-visited-tab${active ? ' admin-visited-tab--active' : ''}${tab.closable ? ' admin-visited-tab--closable' : ''}`}
      >
        <button
          type="button"
          role={measure ? undefined : 'tab'}
          aria-selected={measure ? undefined : active}
          className="admin-visited-tab__main"
          title={tab.group ? `${tab.group} / ${tab.label}` : tab.label}
          onClick={measure ? undefined : () => activateTab(tab)}
        >
          <span className="admin-visited-tab__dot" aria-hidden="true" />
          <span className="admin-visited-tab__label">{tab.label}</span>
        </button>
        {tab.closable ? (
          <button
            type="button"
            className="admin-visited-tab__close"
            aria-label={`关闭${tab.label}`}
            title={`关闭${tab.label}`}
            onClick={measure ? undefined : event => {
              event.stopPropagation();
              closeTab(tab.id);
            }}
          >
            <CloseOutlined />
          </button>
        ) : null}
      </div>
    );
  };

  const overflowItems = hiddenTabs.map(tab => ({
    key: tab.id,
    label: <span title={tab.group ? `${tab.group} / ${tab.label}` : tab.label}>{tab.label}</span>,
    onClick: () => activateTab(tab),
  }));

  return (
    <div ref={tabsViewportRef} className="admin-visited-tabs" aria-label="已打开页面">
      <div className="admin-visited-tabs__scroll" role="tablist">
        {visibleTabs.map(tab => renderTab(tab))}
        {hiddenTabs.length > 0 ? (
          <Dropdown menu={{ items: overflowItems }} trigger={['click']} placement="bottomLeft">
            <button
              type="button"
              className="admin-visited-tabs__more"
              aria-label={`更多已打开页面，共${hiddenTabs.length}个`}
            >
              <MoreOutlined />
              <span>更多（{hiddenTabs.length}）</span>
            </button>
          </Dropdown>
        ) : null}
      </div>
      <div ref={measureRef} className="admin-visited-tabs__measure" aria-hidden="true">
        {tabs.map(tab => renderTab(tab, true))}
        <button
          type="button"
          className="admin-visited-tabs__more"
          data-more-measure
          tabIndex={-1}
        >
          <MoreOutlined />
          <span>更多（{tabs.length}）</span>
        </button>
      </div>
    </div>
  );
}
